/**
 * W-31 §8.3 parsing rules — the delimited-text half, as pure functions so the
 * worker, the tests and (in a pinch) the main thread all read a file the same
 * way.
 *
 * Only CSV / TSV / TXT are read here. An XLSX is a ZIP of XML: `sniffFormat`
 * reports it as `xlsx` and the worker hands those bytes to ExcelJS instead (the
 * library-free half of that path is `xlsx.ts`), so the rows arrive in the same
 * shape whichever file the owner dropped.
 *
 * The row reader is a generator: the worker pulls rows a slice at a time and
 * reports progress between slices, so a 2.000-row file never blocks a frame.
 */

export const BOM = '﻿';

/** Delimiters tried on the header line, in the order Turkish Excel prefers. */
export const DELIMITERS = [';', '\t', ','] as const;
export type Delimiter = (typeof DELIMITERS)[number];

export type FileFormat = 'csv' | 'tsv' | 'xlsx' | 'unsupported';

/**
 * What the dropzone accepts, by extension — the browser reports inconsistent
 * MIME types for CSV (text/csv, application/vnd.ms-excel, empty), so the
 * extension is the only reliable signal.
 */
export function sniffFormat(fileName: string): FileFormat {
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  if (ext === 'csv') return 'csv';
  if (ext === 'tsv' || ext === 'txt' || ext === 'tab') return 'tsv';
  // .xls is OLE2/BIFF, not a zip — ExcelJS cannot open it, so it is an unsupported type, not a broken xlsx
  if (ext === 'xlsx' || ext === 'xlsm') return 'xlsx';
  return 'unsupported';
}

/**
 * The content type the archive copy is stored with. The browser's own `File.type`
 * cannot be used: Windows reports `application/vnd.ms-excel` for a CSV (which the
 * `imports` bucket does not allow, migration 20260922000004) and sometimes
 * nothing at all, and a workbook stored as `text/plain` downloads as a text file
 * that Excel refuses to open. These four are exactly the bucket's list, without
 * parameters — the allow-list is matched literally.
 */
const FORMAT_MIME: Record<Exclude<FileFormat, 'unsupported'>, string> = {
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export function importFileMime(format: FileFormat): string {
  return format === 'unsupported' ? 'text/plain' : FORMAT_MIME[format];
}

export function stripBom(text: string): string {
  return text.startsWith(BOM) ? text.slice(1) : text;
}

/**
 * UTF-8 first (with the BOM dropped), Windows-1254 as the fallback — that is
 * what Turkish Excel writes when "CSV (MS-DOS)" is chosen. A file whose UTF-8
 * decoding contains U+FFFD was not UTF-8.
 */
export function decodeSpreadsheetText(bytes: Uint8Array | ArrayBuffer): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const utf8 = new TextDecoder('utf-8').decode(view);
  if (!utf8.includes('�')) return stripBom(utf8);
  try {
    return stripBom(new TextDecoder('windows-1254').decode(view));
  } catch {
    return stripBom(utf8);
  }
}

/** The first line of the text, ignoring a leading BOM and blank lines. */
export function firstLine(text: string): string {
  const body = stripBom(text);
  for (const line of body.split(/\r?\n/)) if (line.trim() !== '') return line;
  return '';
}

/**
 * The delimiter that splits the header line into the most columns; ties go to
 * the earlier entry of DELIMITERS. Characters inside quotes do not count.
 */
export function detectDelimiter(headerLine: string): Delimiter {
  let best: Delimiter = ';';
  let bestCount = 0;
  for (const d of DELIMITERS) {
    let count = 0;
    let quoted = false;
    for (let i = 0; i < headerLine.length; i += 1) {
      const ch = headerLine[i];
      if (ch === '"') quoted = !quoted;
      else if (!quoted && ch === d) count += 1;
    }
    if (count > bestCount) {
      best = d;
      bestCount = count;
    }
  }
  return best;
}

/**
 * RFC 4180 rows: `""` inside a quoted field is one quote, a newline inside
 * quotes stays in the value, and CRLF or LF both end a row. Cells are trimmed —
 * Excel pads them and every consumer here would trim anyway.
 */
export function* iterateRows(text: string, delimiter: string): Generator<string[]> {
  const body = stripBom(text);
  let cells: string[] = [];
  let cell = '';
  let quoted = false;
  let started = false;
  const pushCell = () => {
    cells.push(cell.trim());
    cell = '';
  };
  for (let i = 0; i < body.length; i += 1) {
    const ch = body[i]!;
    if (quoted) {
      if (ch === '"') {
        if (body[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') {
      quoted = true;
      started = true;
      continue;
    }
    if (ch === delimiter) {
      pushCell();
      started = true;
      continue;
    }
    if (ch === '\r') continue;
    if (ch === '\n') {
      pushCell();
      yield cells;
      cells = [];
      started = false;
      continue;
    }
    cell += ch;
    started = true;
  }
  if (started || cell !== '' || cells.length > 0) {
    pushCell();
    yield cells;
  }
}

export function isBlankRow(cells: string[]): boolean {
  return cells.every((c) => c === '');
}

export interface ParseOptions {
  delimiter?: Delimiter;
  /** hard stop so a pasted 200.000-row export cannot exhaust memory */
  maxRows?: number;
}

export interface Table {
  header: string[];
  rows: string[][];
  /** rows past `maxRows`, dropped — the wizard refuses the file when > 0 */
  overflow: number;
}

export interface DelimitedTable extends Table {
  delimiter: Delimiter;
}

/** How often `buildTable` reports its count — often enough to look live, rarely enough to be free. */
export const PROGRESS_EVERY = 250;

/**
 * The §8.3 row rules, shared by every file format: header = first non-blank row
 * (its trailing unnamed columns dropped, so a sheet Excel padded still matches
 * the template exactly), blank rows dropped silently, every row padded to the
 * header width so a mapping index is always in range, and everything past
 * `maxRows` counted instead of kept.
 *
 * `source` is pulled lazily, so the CSV generator and the XLSX row walker both
 * hand rows over one at a time and the worker can report progress between them.
 */
export function buildTable(source: Iterable<string[]>, maxRows: number, onProgress?: (rows: number) => void): Table {
  let header: string[] | null = null;
  const rows: string[][] = [];
  let overflow = 0;
  for (const cells of source) {
    if (isBlankRow(cells)) continue;
    if (!header) {
      header = [...cells];
      while (header.length > 0 && header[header.length - 1] === '') header.pop();
      continue;
    }
    if (rows.length >= maxRows) {
      overflow += 1;
      continue;
    }
    rows.push(padRow(cells, header.length));
    if (onProgress && rows.length % PROGRESS_EVERY === 0) onProgress(rows.length);
  }
  return { header: header ?? [], rows, overflow };
}

/** Whole-file parse of delimited text. */
export function parseDelimited(text: string, opts: ParseOptions = {}): DelimitedTable {
  const delimiter = opts.delimiter ?? detectDelimiter(firstLine(text));
  const table = buildTable(iterateRows(text, delimiter), opts.maxRows ?? 100_000);
  return { ...table, delimiter };
}

export function padRow(cells: string[], width: number): string[] {
  if (cells.length === width) return cells;
  if (cells.length > width) return cells.slice(0, width);
  return [...cells, ...Array.from({ length: width - cells.length }, () => '')];
}

/** "24 KB" / "1,2 MB" — the file-row meta line of W-31c step 1. */
export function formatFileSize(bytes: number, locale: 'tr' | 'en' = 'tr'): string {
  const mb = bytes / 1_048_576;
  if (mb >= 1) return `${mb.toLocaleString(locale === 'tr' ? 'tr-TR' : 'en-GB', { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * SHA-256 of the file as lowercase hex — `import_batches.file_sha256`, which is
 * what "Bu dosya {{when}} tarihinde … zaten işlendi" compares. `crypto.subtle`
 * is unavailable over plain HTTP, so the caller treats null as "cannot tell".
 */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string | null> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}
